import React, { useMemo, useState } from "react";
import { RotateCcw } from "lucide-react";
import { getAllStatuses, setItemStatus, resetStatuses } from "../../utils/courseLocalProgress";

interface LessonFlashcardsProps {
  cards: { term: string; definition: string }[];
  scopeId: string;
}

const LessonFlashcards: React.FC<LessonFlashcardsProps> = ({ cards, scopeId }) => {
  const [index, setIndex] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [statuses, setStatuses] = useState<Record<string, boolean>>(() =>
    getAllStatuses("flashcards", scopeId)
  );

  const knownCount = useMemo(
    () => cards.filter((card) => statuses[card.term]).length,
    [cards, statuses]
  );

  if (cards.length === 0) return null;
  const card = cards[index];

  const advance = () => {
    setFlipped(false);
    setIndex((prev) => (prev + 1) % cards.length);
  };

  const mark = (known: boolean) => {
    setItemStatus("flashcards", scopeId, card.term, known);
    setStatuses((prev) => ({ ...prev, [card.term]: known }));
    advance();
  };

  const handleReset = () => {
    resetStatuses("flashcards", scopeId);
    setStatuses({});
  };

  return (
    <div className="lesson-flashcards">
      <div className="lesson-flashcards__progress">
        <span>
          {knownCount} of {cards.length} marked known
        </span>
        <button type="button" onClick={handleReset} className="lesson-flashcards__reset">
          <RotateCcw aria-hidden="true" size={14} /> Reset progress
        </button>
      </div>

      <button
        type="button"
        className={`lesson-flashcards__card ${flipped ? "is-flipped" : ""}`}
        onClick={() => setFlipped((prev) => !prev)}
        aria-label={flipped ? `Definition: ${card.definition}` : `Term: ${card.term}. Press to reveal definition.`}
      >
        <span className="lesson-flashcards__label">{flipped ? "Definition" : "Term"}</span>
        <span className="lesson-flashcards__content">{flipped ? card.definition : card.term}</span>
        <span className="lesson-flashcards__hint">{flipped ? "Click to see the term again" : "Click to reveal"}</span>
      </button>

      <p className="lesson-flashcards__count">
        Card {index + 1} of {cards.length}
      </p>

      <div className="lesson-flashcards__actions">
        <button type="button" onClick={() => mark(false)} className="lesson-flashcards__review">
          Needs review
        </button>
        <button type="button" onClick={() => mark(true)} className="lesson-flashcards__known">
          I know this
        </button>
      </div>
    </div>
  );
};

export default LessonFlashcards;
